import type { LucideIcon } from "lucide-react";
import { ChevronRight, MoreHorizontal } from "lucide-react";
import type { CSSProperties, ReactElement, ReactNode } from "react";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { NavLink, useLocation, useParams } from "react-router-dom";

import { Heading } from "@/components/ui/heading";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { useIsMobile } from "@/hooks/useIsMobile";
import { useAuth } from "@/hooks/useAuth";
import { usePermissions } from "@/hooks/usePermissions";
import { resolveAccountSidebarNav } from "@/navigation/accountNavConfig";
import { isAppHomeNavActive, resolveAppHomeNavLinks } from "@/navigation/appNavConfig";
import {
  getSettingsBottomNavMore,
  getSettingsBottomNavPrimary,
  type SettingsBottomNavMoreItem,
} from "@/navigation/settingsNavConfig";
import { useSidebarNavState } from "@/navigation/sidebarRegistry";
import type { SidebarNavGroupItem, SidebarNavLinkItem } from "@/navigation/types";
import { groupContainsPath } from "@/navigation/navItems";
import { TOUR_ANCHOR } from "@/components/product-tour/tourSteps";
import { cn } from "@/lib/utils";
import {
  getProjectBottomNavMore,
  getProjectBottomNavPrimary,
} from "@/pages/Project/projectNavConfig";

const bottomNavShellClassName =
  "pointer-events-none fixed inset-x-0 bottom-0 z-40 flex justify-center px-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] lg:hidden";

const bottomNavBarClassName =
  "bottom-nav-glass pointer-events-auto flex h-[3.25rem] w-full items-center justify-around gap-1 rounded-full border border-white/25 px-2 shadow-[0_8px_24px_-8px_rgba(0,0,0,0.08)] dark:border-white/10";

const bottomNavIconClassName = "size-5 shrink-0 stroke-[2.25]";

const tabClassName = ({
  isActive,
  labeled = false,
}: {
  isActive: boolean;
  labeled?: boolean;
}): string =>
  cn(
    "flex h-11 min-w-0 flex-1 items-center justify-center rounded-full transition-all duration-200",
    labeled ? "gap-1.5 px-3" : null,
    isActive
      ? "bg-primary text-primary-foreground shadow-sm"
      : "text-foreground/90 hover:bg-foreground/5 hover:text-foreground [&_svg]:drop-shadow-[0_1px_0_rgba(255,255,255,0.85)] dark:text-foreground/95 dark:[&_svg]:drop-shadow-[0_1px_0_rgba(0,0,0,0.35)]",
  );

const bottomNavGlassStyle: CSSProperties = {
  backgroundColor: "rgba(255, 255, 255, 0.06)",
  WebkitBackdropFilter: "blur(8px) saturate(130%)",
  backdropFilter: "blur(8px) saturate(130%)",
};

function BottomNavShell({
  "aria-label": ariaLabel,
  children,
  dataTour,
}: {
  "aria-label": string;
  children: ReactNode;
  dataTour?: string;
}): ReactElement {
  return createPortal(
    <nav className={bottomNavShellClassName} aria-label={ariaLabel}>
      <div className={bottomNavBarClassName} style={bottomNavGlassStyle} data-tour={dataTour}>
        {children}
      </div>
    </nav>,
    document.body,
  );
}

function BottomNavTab({
  to,
  end,
  icon: Icon,
  label,
  onNavigate,
  showLabel = false,
  forceActive,
}: {
  to: string;
  end?: boolean;
  icon: LucideIcon;
  label: string;
  onNavigate?: () => void;
  showLabel?: boolean;
  forceActive?: boolean;
}): ReactElement {
  return (
    <NavLink
      to={to}
      end={end}
      onClick={onNavigate}
      aria-label={showLabel ? undefined : label}
      className={({ isActive }) =>
        tabClassName({ isActive: forceActive ?? isActive, labeled: showLabel })
      }
    >
      <Icon className={bottomNavIconClassName} aria-hidden />
      {showLabel ? <span className="truncate text-xs font-medium">{label}</span> : null}
    </NavLink>
  );
}

function AccountBottomNav(): ReactElement | null {
  const { t } = useTranslation();
  const sections = resolveAccountSidebarNav({ params: {}, can: () => true });
  const items = sections.flatMap((section) => section.items).filter((item) => item.kind === "link");

  if (items.length === 0) return null;

  return (
    <BottomNavShell aria-label={t("account.title")}>
      {items.map((item) => {
        const Icon = item.icon;
        return (
          <BottomNavTab
            key={item.to}
            to={item.to}
            end={item.end}
            icon={Icon}
            label={t(item.labelKey)}
          />
        );
      })}
    </BottomNavShell>
  );
}

function BottomNavMoreSheet({
  title,
  listClassName,
  children,
}: {
  title: string;
  listClassName?: string;
  children: ReactNode;
}): ReactElement {
  return (
    <SheetContent
      side="bottom"
      className="max-h-[min(85dvh,100%)] gap-0 overflow-y-auto overscroll-y-contain p-0 touch-pan-y"
    >
      <SheetTitle className="sr-only">{title}</SheetTitle>
      <div className="sticky top-0 z-10 border-b border-border bg-background px-4 py-3">
        <Heading variant="panelStrong" as="p">
          {title}
        </Heading>
      </div>
      <div className={listClassName}>{children}</div>
    </SheetContent>
  );
}

function SettingsBottomNav(): ReactElement | null {
  const { t } = useTranslation();
  const { can } = usePermissions();
  const { user } = useAuth();
  const { pathname } = useLocation();
  const [moreOpen, setMoreOpen] = useState(false);
  const showOrgBilling = Boolean(
    user?.organization && user.isOrgAdmin && !user.organization.billingExempt,
  );

  useEffect(() => {
    setMoreOpen(false);
  }, [pathname]);

  const primaryItems = getSettingsBottomNavPrimary(can, { showOrgBilling });
  const moreItems = getSettingsBottomNavMore(can, { showOrgBilling });

  if (primaryItems.length === 0 && moreItems.length === 0) return null;

  const isMoreActive = moreItems.some((item) => moreItemContainsPath(item, pathname));
  const dismissMore = (): void => setMoreOpen(false);

  return (
    <>
      {moreItems.length > 0 ? (
        <Sheet open={moreOpen} onOpenChange={setMoreOpen}>
          <BottomNavMoreSheet
            title={t("settings.title")}
            listClassName="flex flex-col gap-0.5 px-2 py-2 pb-[max(0.5rem,env(safe-area-inset-bottom))]"
          >
            {moreItems.map((item) =>
              item.kind === "group" ? (
                <SettingsMoreNavGroup key={item.labelKey} item={item} onNavigate={dismissMore} />
              ) : (
                <SettingsMoreNavItem
                  key={item.to}
                  item={item}
                  label={t(item.labelKey)}
                  onNavigate={dismissMore}
                />
              ),
            )}
          </BottomNavMoreSheet>
        </Sheet>
      ) : null}

      <BottomNavShell aria-label={t("settings.title")}>
        {primaryItems.map((item) => {
          const Icon = item.icon;
          return (
            <BottomNavTab
              key={item.to}
              to={item.to}
              end={item.end}
              icon={Icon}
              label={t(item.labelKey)}
            />
          );
        })}
        {moreItems.length > 0 ? (
          <button
            type="button"
            onClick={() => setMoreOpen(true)}
            className={tabClassName({ isActive: isMoreActive })}
            aria-label={t("common.more")}
            aria-expanded={moreOpen}
            aria-haspopup="dialog"
          >
            <MoreHorizontal className={bottomNavIconClassName} aria-hidden />
          </button>
        ) : null}
      </BottomNavShell>
    </>
  );
}

function moreItemContainsPath(item: SettingsBottomNavMoreItem, pathname: string): boolean {
  if (item.kind === "link") {
    return pathname === item.to || pathname.startsWith(`${item.to}/`);
  }
  return groupContainsPath(item, pathname);
}

function SettingsMoreNavGroup({
  item,
  onNavigate,
  nested = false,
}: {
  item: SidebarNavGroupItem;
  onNavigate: () => void;
  nested?: boolean;
}): ReactElement {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const Icon = item.icon;

  const isGroupActive = groupContainsPath(item, pathname);
  const [expanded, setExpanded] = useState(isGroupActive);
  const [wasGroupActive, setWasGroupActive] = useState(isGroupActive);

  if (isGroupActive !== wasGroupActive) {
    setWasGroupActive(isGroupActive);
    if (isGroupActive) setExpanded(true);
  }

  return (
    <div>
      <button
        type="button"
        aria-expanded={expanded}
        onClick={() => setExpanded((open) => !open)}
        className={cn(
          "flex w-full items-center gap-3 rounded-xl px-3 py-3 text-left text-sm transition-colors",
          nested && "pl-11",
          isGroupActive
            ? "font-medium text-foreground [&_svg]:text-primary"
            : "text-foreground hover:bg-accent/60 [&_svg]:text-muted-foreground",
        )}
      >
        {Icon && !nested ? <Icon className={bottomNavIconClassName} aria-hidden /> : null}
        <span className="min-w-0 flex-1 truncate">{t(item.labelKey)}</span>
        <ChevronRight
          className={cn(
            "size-4 shrink-0 text-muted-foreground transition-transform duration-200",
            expanded && "rotate-90",
          )}
          aria-hidden
        />
      </button>
      {expanded ? (
        <div className="flex flex-col gap-0.5" role="group" aria-label={t(item.labelKey)}>
          {item.items.map((sub) => {
            if (sub.kind === "group") {
              return (
                <SettingsMoreNavGroup
                  key={sub.labelKey}
                  item={sub}
                  onNavigate={onNavigate}
                  nested
                />
              );
            }
            const SubIcon = sub.icon;
            if (!Icon && SubIcon) {
              return (
                <div key={sub.to} className="pl-4">
                  <SettingsMoreNavItem
                    item={{
                      kind: "link",
                      to: sub.to,
                      end: sub.end,
                      labelKey: sub.labelKey,
                      icon: SubIcon,
                      permission: sub.permission,
                    }}
                    label={t(sub.labelKey)}
                    onNavigate={onNavigate}
                  />
                </div>
              );
            }
            return (
              <NavLink
                key={sub.to}
                to={sub.to}
                end={sub.end}
                onClick={onNavigate}
                className={({ isActive }) =>
                  cn(
                    "rounded-xl py-2.5 pr-3 text-sm transition-colors",
                    nested ? "pl-14" : "pl-11",
                    isActive
                      ? "bg-accent font-medium text-foreground"
                      : "text-muted-foreground hover:bg-accent/60 hover:text-foreground",
                  )
                }
              >
                {t(sub.labelKey)}
              </NavLink>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

function SettingsMoreNavItem({
  item,
  label,
  onNavigate,
}: {
  item: SidebarNavLinkItem;
  label: string;
  onNavigate: () => void;
}): ReactElement {
  const Icon = item.icon;
  return (
    <NavLink
      to={item.to}
      end={item.end}
      onClick={onNavigate}
      className={({ isActive }) =>
        cn(
          "flex items-center gap-3 rounded-xl px-3 py-3 text-sm transition-colors",
          isActive
            ? "bg-accent font-medium text-foreground [&_svg]:text-primary"
            : "text-foreground hover:bg-accent/60 [&_svg]:text-muted-foreground",
        )
      }
    >
      <Icon className={bottomNavIconClassName} aria-hidden />
      <span className="min-w-0 flex-1 truncate">{label}</span>
    </NavLink>
  );
}

function AppBottomNav(): ReactElement | null {
  const { t } = useTranslation();
  const { can } = usePermissions();
  const { pathname } = useLocation();
  const items = resolveAppHomeNavLinks({ params: {}, can });

  if (items.length === 0) return null;

  return (
    <BottomNavShell aria-label={t("common.mainNav")} dataTour={TOUR_ANCHOR.appNav}>
      {items.map((item) => {
        const Icon = item.icon;
        return (
          <BottomNavTab
            key={item.to}
            to={item.to}
            end={item.end}
            icon={Icon}
            label={t(item.labelKey)}
            showLabel
            forceActive={isAppHomeNavActive(pathname, item.to, item.end)}
          />
        );
      })}
    </BottomNavShell>
  );
}

function projectSectionTo(projectId: string, suffix: string): string {
  return `/projects/${projectId}/${suffix}`;
}

function ProjectBottomNav(): ReactElement | null {
  const { t } = useTranslation();
  const { can } = usePermissions();
  const { projectId } = useParams<{ projectId: string }>();
  const { pathname } = useLocation();
  const [moreOpen, setMoreOpen] = useState(false);

  useEffect(() => {
    setMoreOpen(false);
  }, [pathname]);

  if (!projectId) return null;

  const primarySections = getProjectBottomNavPrimary(can);
  const moreItems = getProjectBottomNavMore(projectId, can);

  const isMoreActive = moreItems.some((item) => moreItemContainsPath(item, pathname));

  const dismissMore = (): void => setMoreOpen(false);

  return (
    <>
      {moreItems.length > 0 ? (
        <Sheet open={moreOpen} onOpenChange={setMoreOpen}>
          <BottomNavMoreSheet
            title={t("project.navLabel")}
            listClassName="flex flex-col gap-0.5 px-2 py-2 pb-[max(0.5rem,env(safe-area-inset-bottom))]"
          >
            {moreItems.map((item) => (
              <SettingsMoreNavGroup key={item.labelKey} item={item} onNavigate={dismissMore} />
            ))}
          </BottomNavMoreSheet>
        </Sheet>
      ) : null}

      <BottomNavShell aria-label={t("project.navLabel")}>
        {primarySections.map((section) => (
          <BottomNavTab
            key={section.suffix}
            to={projectSectionTo(projectId, section.suffix)}
            icon={section.icon}
            label={t(section.labelKey)}
          />
        ))}
        {moreItems.length > 0 ? (
          <button
            type="button"
            onClick={() => setMoreOpen(true)}
            className={tabClassName({ isActive: isMoreActive })}
            aria-label={t("common.more")}
            aria-expanded={moreOpen}
            aria-haspopup="dialog"
          >
            <MoreHorizontal className={bottomNavIconClassName} aria-hidden />
          </button>
        ) : null}
      </BottomNavShell>
    </>
  );
}

export function BottomNav(): ReactElement | null {
  const isMobile = useIsMobile();
  const { sidebarId } = useSidebarNavState();

  if (!isMobile) return null;
  if (sidebarId === "project") return <ProjectBottomNav />;
  if (sidebarId === "settings") return <SettingsBottomNav />;
  if (sidebarId === "account") return <AccountBottomNav />;
  if (sidebarId === "app") return <AppBottomNav />;
  return null;
}
